/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {globalGroupSelector} from '@ion/applications/src/store/selectors/global-group';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CRUDPermissions} from '@ion/core/src/types/entities';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';

export interface AccountEditProps {
  editGroup?: Function;
  isLoggedinUserAccount?: boolean;
  accountsPermissions?: CRUDPermissions;
}

export const useStyles = makeStyles((theme: DLSTheme) => ({
  adminCtas : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  groupName : {
    color : dlsColor('black'),
    ...dlsAlt('typography', 'cta2')
  }
}));

const AccountEdit = ({
  editGroup,
  accountsPermissions,
  isLoggedinUserAccount
}: AccountEditProps) => {
  const classes           = useStyles();
  const globalGroup       = useSelector(globalGroupSelector);
  const currentAccountId  = entityId(globalGroup);
  const groupName         = globalGroup?.title || '';
  const {
    hasReadPermission,
    hasUpdatePermission
  } = accountsPermissions || {};

  const onEdit = useCallback(function() {
    editGroup(currentAccountId, false);
  }, [currentAccountId, editGroup]);

  const onView = useCallback(function() {
    editGroup(currentAccountId, true);
  }, [currentAccountId, editGroup]);

  return (
    <div className={classes.adminCtas}>
      <LabelTypography className={classes.groupName}>
        {translate('common:editors.shared.currentAccount', {account: groupName})}
      </LabelTypography>
      <TertiaryButton fontSize={'12px'}
        onClick={onView}
        disabled={!hasReadPermission}>
        <span>{translate('common:view')}</span>
      </TertiaryButton>
      <TertiaryButton fontSize={'12px'}
        onClick={onEdit}
        disabled={!hasUpdatePermission}>
        <span>{translate('common:edit')}</span>
      </TertiaryButton>
    </div>
  );
};

export default React.memo(AccountEdit);
